import {
  Sparkles,
  X,
  Send,
  Loader2,
  Minimize2,
  Maximize2,
  WifiOff,
  RefreshCw,
  Mic,
  MicOff,
  Paperclip,
  AudioLines,
} from 'lucide-react';
import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { supabase } from '../lib/supabase';
import { useAuth } from '../providers/AuthProvider';

import { GenerativeSurface } from './GenerativeSurface/GenerativeSurface';
import { NexusVoiceSession } from './NexusVoiceSession';

// Faza 4 (Jarvis) — generative UI. When a Nexus reply carries `render_directives`
// they render as native widgets (charts/tables/metrics/…) below the prose.
// Default-ON; set VITE_GENERATIVE_UI_ENABLED=false to force plain-text chat.
const GENERATIVE_UI_ENABLED = import.meta.env.VITE_GENERATIVE_UI_ENABLED !== 'false';

export interface ChatMessage {
  id: string;
  role: 'user' | 'nexus';
  content: string;
  timestamp: Date;
  status?: 'sent' | 'error';
  // Faza 4: optional AI-emitted render-directives (validated before render).
  directives?: unknown;
}

type ConnectionStatus = 'connected' | 'connecting' | 'offline';

const NEXUS_GREETING =
  'Witaj! Jestem NEXUS — dusza systemu INSOLITUM Universe. Mogę rozmawiać tekstem, głosem, a także analizować obrazy i video. W czym mogę Ci pomóc?';

// OpenClaw Gateway API — primary endpoint (routed through Vercel proxy)
const OPENCLAW_API_URL =
  import.meta.env.VITE_NEXUS_ASSISTANT_API_URL ||
  import.meta.env.VITE_OPENCLAW_API_URL ||
  '/openclaw-api';

// Nexus-core API direct — fallback when openclaw-gateway is unavailable
const NEXUS_API_URL = import.meta.env.VITE_NEXUS_API_URL || '/nexus-api';

// Supabase Edge Function URL for last-resort fallback
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

const MAX_RETRIES = 3;
const RETRY_BASE_DELAY = 1000;
const HEALTH_CHECK_INTERVAL = 60_000; // Check health every 60s

async function fetchWithRetry(
  url: string,
  options: RequestInit,
  retries = MAX_RETRIES
): Promise<Response> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60_000); // 60s — AI completions can take 20s+

      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) return response;

      // Don't retry on 4xx (client errors)
      if (response.status >= 400 && response.status < 500) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // On 5xx with a structured error body, the failure is deterministic
      // (e.g. AI provider down) — skip retries and throw immediately so the
      // caller can try the Supabase Edge Function fallback instead.
      if (response.status >= 500) {
        try {
          const errorBody = await response.json();
          if (errorBody?.error) {
            throw new Error(errorBody.error);
          }
        } catch (e) {
          // Re-throw structured errors; only swallow JSON parse failures
          if (!(e instanceof SyntaxError)) throw e;
        }
      }

      // Unstructured 5xx — possibly transient, retry
      lastError = new Error(`HTTP ${response.status}`);
    } catch (err) {
      lastError = err as Error;
      if ((err as Error).name === 'AbortError') {
        // Timeout — don't retry, fall through to caller's fallback immediately
        throw new Error('Request timeout');
      } else if (
        err instanceof Error &&
        err.name !== 'AbortError' &&
        !err.message.startsWith('HTTP ')
      ) {
        // Structured error from server — don't retry, throw immediately
        throw err;
      }
    }

    if (attempt < retries - 1) {
      const delay = RETRY_BASE_DELAY * Math.pow(2, attempt);
      await new Promise((r) => setTimeout(r, delay));
    }
  }

  throw lastError || new Error('Request failed');
}

export interface NexusChatWidgetProps {
  /**
   * Render the chat surface inline (no floating orb / fixed positioning) so it
   * can be embedded directly in a page — e.g. the live Jarvis dashboard. When
   * omitted the component behaves exactly as the global floating widget.
   */
  inline?: boolean;
  /**
   * When set (inline mode), auto-send this prompt ONCE on mount as a real Nexus
   * turn so the assistant greets first. Uses the same send path as the composer;
   * its response (prose + render_directives) renders on the page.
   */
  autoBriefingPrompt?: string;
  /**
   * Feature E (Super-Nexus): start the super-admin scope selector in the
   * "Wszyscy tenanci" (platform) position instead of the persisted per-tenant
   * choice. Only meaningful for super-admins; ignored otherwise. Does NOT
   * overwrite the persisted default — it is a per-surface starting point
   * (SuperNexusPage), the user can still switch to a single tenant.
   */
  defaultPlatformScope?: boolean;
  /**
   * Kokpit "Live Artifacts": render ONLY the composer (input + voice + upload
   * + scope) as a transparent glass bar — no header, no message list. The full
   * conversation is streamed to the host via `onConversation`, which renders
   * Nexus turns as floating artifacts over the universe scene. Implies inline.
   */
  artifactMode?: boolean;
  /**
   * Fired whenever the conversation changes (all send paths: text, stream,
   * fallbacks, voice, vision) and when the typing indicator flips. Lets a host
   * surface render the turns itself (Live Artifacts) while this component
   * keeps owning transport, auth, scope and voice.
   */
  onConversation?: (messages: ChatMessage[], isTyping: boolean) => void;
}

/**
 * Sentinel value in the super-admin scope selector meaning "Wszyscy tenanci".
 * Chat calls then run against the seeded platform org (migration 132) and
 * carry `scope: 'platform'`, which nexus-core verifies against
 * public.users.is_super_admin before asserting the super_admin persona.
 */
const PLATFORM_SCOPE = '__platform__';
const PLATFORM_ORG_SLUG = 'insolitum-platform';

export function NexusChatWidget({
  inline = false,
  autoBriefingPrompt,
  defaultPlatformScope = false,
  artifactMode = false,
  onConversation,
}: NexusChatWidgetProps = {}) {
  const { t } = useTranslation();
  const { user, currentOrganization } = useAuth();
  const isSuperAdmin = user?.is_super_admin === true;
  // Inline surface is always "open"; the floating widget starts collapsed.
  const [isOpen, setIsOpen] = useState(inline);
  const [isExpanded, setIsExpanded] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('connecting');
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'greeting',
      role: 'nexus',
      content: NEXUS_GREETING,
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const healthCheckRef = useRef<ReturnType<typeof setInterval>>();

  // Voice state
  const [isRecording, setIsRecording] = useState(false);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  // File upload ref
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Voice conversation mode
  const [isVoiceSessionOpen, setIsVoiceSessionOpen] = useState(false);

  // Super-admin NEXUS scope. A super-admin account has no organization
  // membership (it is a platform operator, not a tenant member), so
  // `currentOrganization` is null and the assistant would otherwise refuse to
  // send. The operator picks which tenant NEXUS works on; the choice is
  // persisted in system_settings (key 'nexus_superadmin') so it survives
  // reloads, and every conversation/memory row is tagged with that tenant id.
  const [saOrgs, setSaOrgs] = useState<{ id: string; name: string; slug: string | null }[]>([]);
  const [saScopeOrgId, setSaScopeOrgId] = useState<string>('');

  // Feature E: "Wszyscy tenanci" — the selector holds the PLATFORM_SCOPE
  // sentinel; calls run against the seeded platform org with scope='platform'.
  const isPlatformScope = isSuperAdmin && saScopeOrgId === PLATFORM_SCOPE;
  const platformOrgId = saOrgs.find((o) => o.slug === PLATFORM_ORG_SLUG)?.id || '';

  // Tenant id used for every NEXUS call. Super-admins: the scope selector
  // wins — after migration 132 they DO have a currentOrganization (the seeded
  // platform org, or another default), so preferring it would make the
  // selector a no-op and could write "Wszyscy tenanci" conversations under a
  // non-platform default org. Regular users: their current org.
  const effectiveTenantId = isSuperAdmin
    ? (isPlatformScope ? platformOrgId : saScopeOrgId) || currentOrganization?.organization_id || ''
    : currentOrganization?.organization_id || '';

  // Chat scope sent to the backend (verified there against public.users).
  const chatScope: 'platform' | undefined = isPlatformScope ? 'platform' : undefined;

  // Load the organization list + persisted scope for super-admins.
  useEffect(() => {
    if (!isSuperAdmin) return;
    let cancelled = false;
    (async () => {
      const [{ data: orgs }, { data: setting }] = await Promise.all([
        supabase.from('organizations').select('id, name, slug').order('name'),
        supabase
          .from('system_settings')
          .select('value')
          .eq('key', 'nexus_superadmin')
          .maybeSingle(),
      ]);
      if (cancelled) return;
      const list = (orgs as { id: string; name: string; slug: string | null }[]) || [];
      setSaOrgs(list);
      const savedId = (setting?.value as { default_org_id?: string } | null)?.default_org_id || '';
      setSaScopeOrgId((defaultPlatformScope ? PLATFORM_SCOPE : '') || savedId || list[0]?.id || '');
    })();
    return () => {
      cancelled = true;
    };
    // defaultPlatformScope is a mount-time starting point, not a live control,
    // so it is intentionally not a dependency here.
  }, [isSuperAdmin]);

  // Persist the super-admin's tenant scope and reset the conversation so the
  // new tenant context starts a fresh thread. The PLATFORM_SCOPE sentinel is
  // NOT persisted — other consumers of `default_org_id` expect a real org id,
  // so "Wszyscy tenanci" stays a per-session (or per-surface) choice.
  const handleScopeChange = (orgId: string) => {
    setSaScopeOrgId(orgId);
    setConversationId(null);
    if (orgId === PLATFORM_SCOPE) return;
    if (user) {
      void supabase.from('system_settings').upsert(
        {
          key: 'nexus_superadmin',
          value: { default_org_id: orgId, updated_by: user.id },
          updated_by: user.id,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'key' }
      );
    }
  };

  // Health check: verify backend is reachable
  const checkHealth = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(`${OPENCLAW_API_URL}/health`, {
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        setConnectionStatus('connected');
        return true;
      }
      setConnectionStatus('offline');
      return false;
    } catch {
      setConnectionStatus('offline');
      return false;
    }
  }, []);

  // Run health check on mount and periodically
  useEffect(() => {
    checkHealth();
    healthCheckRef.current = setInterval(checkHealth, HEALTH_CHECK_INTERVAL);
    return () => {
      if (healthCheckRef.current) clearInterval(healthCheckRef.current);
    };
  }, [checkHealth]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // Live Artifacts: stream the conversation to the host surface. Covers every
  // append path (text/stream/fallbacks/voice/vision) because they all go
  // through setMessages.
  useEffect(() => {
    onConversation?.(messages, isTyping);
    // Deliberately keyed on the conversation itself, not the callback identity.
  }, [messages, isTyping]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isOpen]);

  // Helper to get auth headers. Stable identity (useCallback, no deps — it only
  // touches the module-level supabase client): this function is passed down to
  // NexusVoiceSession, and a fresh identity on every render made each widget
  // re-render (e.g. the periodic health check flipping state) tear down and
  // reconnect the live realtime voice session mid-utterance.
  const getAuthHeaders = useCallback(async (): Promise<Record<string, string>> => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {};
  }, []);

  // Fallback: chat via Supabase Edge Function when backend is down
  const chatViaSupabaseFallback = async (
    text: string,
    tenantId: string,
    userId: string,
    convId: string | null
  ): Promise<{ response: string; conversation_id: string | null }> => {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session?.access_token || !SUPABASE_URL) {
      throw new Error('No auth session for fallback');
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60_000); // 60s — AI completions can take 20s+

    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/nexus-chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          tenant_id: tenantId,
          user_id: userId,
          message: text,
          conversation_id: convId || undefined,
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) throw new Error(`Fallback HTTP ${response.status}`);

      const data = await response.json();
      return {
        response: data.response,
        conversation_id: data.conversation_id || convId,
      };
    } catch (err) {
      clearTimeout(timeoutId);
      throw err;
    }
  };

  // The backend requires a tenant id; without one every send would be a silent
  // no-op (the exact historical failure when the platform org was missing).
  // Surface it loudly instead so the operator knows WHY nothing happens.
  const reportMissingTenant = () => {
    setMessages((prev) => [
      ...prev,
      {
        id: `nexus-notenant-${Date.now()}`,
        role: 'nexus',
        content: isSuperAdmin
          ? 'Nie mogę ustalić organizacji dla tej rozmowy — wybierz zakres (tenant) powyżej. Jeśli lista jest pusta, brakuje organizacji platformowej (slug insolitum-platform).'
          : 'Twoje konto nie należy do żadnej organizacji — skontaktuj się z administratorem.',
        timestamp: new Date(),
        status: 'error',
      },
    ]);
  };

  const sendMessage = async (explicitText?: string) => {
    const text = (explicitText ?? input).trim();
    if (!text || !user) return;
    if (!effectiveTenantId) {
      reportMissingTenant();
      return;
    }

    const userMsg: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setIsTyping(true);

    const nexusMsgId = `nexus-${Date.now()}`;

    try {
      const authHeaders = await getAuthHeaders();

      // Try SSE streaming via openclaw-gateway first
      const response = await fetch(`${OPENCLAW_API_URL}/api/openclaw/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
        },
        body: JSON.stringify({
          tenant_id: effectiveTenantId,
          message: text,
          conversation_id: conversationId || undefined,
          scope: chatScope,
        }),
      });

      if (!response.ok || !response.body) {
        throw new Error(`HTTP ${response.status}`);
      }

      // Add empty nexus message that we'll stream into
      setMessages((prev) => [
        ...prev,
        { id: nexusMsgId, role: 'nexus', content: '', timestamp: new Date() },
      ]);
      setIsTyping(false); // Hide typing indicator, show streaming content instead

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let streamedContent = '';

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6);
          if (data === '[DONE]') continue;

          try {
            const parsed = JSON.parse(data);

            if (parsed.error) {
              throw new Error(parsed.error);
            }

            if (parsed.token) {
              streamedContent += parsed.token;
              setMessages((prev) =>
                prev.map((m) => (m.id === nexusMsgId ? { ...m, content: streamedContent } : m))
              );
            }

            if (parsed.done) {
              if (parsed.conversation_id) {
                setConversationId(parsed.conversation_id);
              }
              // Faza 4: attach any render-directives carried on the terminal
              // event so the generative surface renders below the streamed prose.
              // GenerativeSurface re-validates, so a malformed payload is inert.
              if (GENERATIVE_UI_ENABLED && parsed.render_directives != null) {
                const directives = parsed.render_directives;
                setMessages((prev) =>
                  prev.map((m) => (m.id === nexusMsgId ? { ...m, directives } : m))
                );
              }
            }
          } catch (e) {
            if (e instanceof SyntaxError) continue; // skip malformed SSE
            throw e;
          }
        }
      }

      // If stream produced no content, show fallback
      if (!streamedContent) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === nexusMsgId
              ? { ...m, content: 'Przepraszam, nie udało mi się wygenerować odpowiedzi.' }
              : m
          )
        );
      }

      setConnectionStatus('connected');
    } catch (streamErr) {
      console.warn('OpenClaw streaming failed, trying fallback...', (streamErr as Error).message);

      // Remove empty streaming message if it was added
      setMessages((prev) => prev.filter((m) => m.id !== nexusMsgId || m.content !== ''));
      setIsTyping(true);

      // Fallback 1: direct nexus-core HTTP
      try {
        const authHeaders = await getAuthHeaders();
        const response = await fetchWithRetry(`${NEXUS_API_URL}/api/nexus/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify({
            tenant_id: effectiveTenantId,
            user_id: user.id,
            message: text,
            conversation_id: conversationId || undefined,
            scope: chatScope,
          }),
        });

        const data = await response.json();
        if (data.conversation_id) setConversationId(data.conversation_id);
        if (data.error && !data.response) throw new Error(data.error);

        setConnectionStatus('connected');
        setMessages((prev) => [
          ...prev,
          {
            id: `nexus-fb-${Date.now()}`,
            role: 'nexus',
            content: data.response || 'Przepraszam, nie udało mi się wygenerować odpowiedzi.',
            timestamp: new Date(),
            directives: GENERATIVE_UI_ENABLED ? data.render_directives : undefined,
          },
        ]);
      } catch {
        // Fallback 2: Supabase Edge Function
        try {
          const fallbackResult = await chatViaSupabaseFallback(
            text,
            effectiveTenantId,
            user.id,
            conversationId
          );
          if (fallbackResult.conversation_id) setConversationId(fallbackResult.conversation_id);
          setConnectionStatus('connected');
          setMessages((prev) => [
            ...prev,
            {
              id: `nexus-fb2-${Date.now()}`,
              role: 'nexus',
              content: fallbackResult.response,
              timestamp: new Date(),
            },
          ]);
        } catch {
          setConnectionStatus('offline');
          setMessages((prev) => [
            ...prev,
            {
              id: `nexus-err-${Date.now()}`,
              role: 'nexus',
              content:
                'Przepraszam, mam chwilowe problemy z połączeniem. Spróbuj ponownie za moment.',
              timestamp: new Date(),
              status: 'error',
            },
          ]);
          setTimeout(checkHealth, 5000);
        }
      }
    } finally {
      setIsTyping(false);
    }
  };

  // ---- Voice Recording ----
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4',
      });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        const mimeType = mediaRecorder.mimeType || 'audio/webm';
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        stream.getTracks().forEach((t) => t.stop());
        await sendVoiceMessage(audioBlob, mimeType);
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (err) {
      console.error('Microphone access denied:', err);
      const errorMsg: ChatMessage = {
        id: `nexus-${Date.now()}`,
        role: 'nexus',
        content: 'Nie udało się uzyskać dostępu do mikrofonu. Sprawdź uprawnienia przeglądarki.',
        timestamp: new Date(),
        status: 'error',
      };
      setMessages((prev) => [...prev, errorMsg]);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  };

  const sendVoiceMessage = async (audioBlob: Blob, mimeType: string) => {
    if (!user) return;
    if (!effectiveTenantId) {
      reportMissingTenant();
      return;
    }
    setIsTyping(true);

    // Convert blob to base64
    const reader = new FileReader();
    const audioBase64 = await new Promise<string>((resolve) => {
      reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
      reader.readAsDataURL(audioBlob);
    });

    // Show user message placeholder
    const userMsgId = `user-${Date.now()}`;
    const userMsg: ChatMessage = {
      id: userMsgId,
      role: 'user',
      content: '\uD83C\uDFA4 Wiadomość głosowa...',
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMsg]);

    try {
      const authHeaders = await getAuthHeaders();

      const response = await fetchWithRetry(`${OPENCLAW_API_URL}/api/openclaw/voice/turn`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
        },
        body: JSON.stringify({
          tenant_id: effectiveTenantId,
          user_id: user.id,
          audio_base64: audioBase64,
          language: 'pl',
          mime_type: mimeType,
          conversation_id: conversationId || undefined,
          synthesize_response: true,
        }),
      });

      const data = await response.json();

      // Update user message with transcription
      setMessages((prev) =>
        prev.map((m) =>
          m.id === userMsgId
            ? { ...m, content: data.transcription || '\uD83C\uDFA4 (nie rozpoznano)' }
            : m
        )
      );

      if (data.conversation_id) setConversationId(data.conversation_id);

      // NEXUS response
      const nexusMsg: ChatMessage = {
        id: `nexus-${Date.now()}`,
        role: 'nexus',
        content: data.response || 'Przepraszam, nie udało mi się odpowiedzieć.',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, nexusMsg]);

      // Play audio response
      if (data.audio_response?.audio_base64) {
        playAudio(data.audio_response.audio_base64);
      }
    } catch (err) {
      console.error('Voice turn failed:', err);
      const errorMsg: ChatMessage = {
        id: `nexus-${Date.now()}`,
        role: 'nexus',
        content: 'Przepraszam, nie udało mi się przetworzyć wiadomości głosowej. Spróbuj ponownie.',
        timestamp: new Date(),
        status: 'error',
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setIsTyping(false);
    }
  };

  const playAudio = (base64Audio: string) => {
    const audio = new Audio(`data:audio/mp3;base64,${base64Audio}`);
    setIsPlayingAudio(true);
    audio.onended = () => setIsPlayingAudio(false);
    audio.onerror = () => setIsPlayingAudio(false);
    audio.play().catch(() => setIsPlayingAudio(false));
  };

  // ---- Document MIME types supported for text extraction ----
  const DOCUMENT_MIME_TYPES = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    'text/plain',
    'text/csv',
    'text/html',
    'text/markdown',
    'application/json',
    'application/xml',
    'text/xml',
    'application/rtf',
  ];

  // ---- Image / Video / Document Upload ----
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user || !effectiveTenantId) return;

    const isVideo = file.type.startsWith('video/');
    const isImage = file.type.startsWith('image/');
    const isDocument = DOCUMENT_MIME_TYPES.includes(file.type);

    if (!isVideo && !isImage && !isDocument) return;

    // For documents: send via chat endpoint with attachments
    if (isDocument) {
      setIsTyping(true);

      const reader = new FileReader();
      const base64Data = await new Promise<string>((resolve) => {
        reader.onloadend = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1] || '');
        };
        reader.readAsDataURL(file);
      });

      const userMsg: ChatMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: `Wysylam dokument do analizy: ${file.name}`,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMsg]);

      try {
        const authHeaders = await getAuthHeaders();
        const response = await fetchWithRetry(`${OPENCLAW_API_URL}/api/openclaw/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify({
            tenant_id: effectiveTenantId,
            message: `Przeanalizuj zalaczony dokument: ${file.name}`,
            conversation_id: conversationId || undefined,
            scope: chatScope,
            attachments: [
              {
                filename: file.name,
                mime_type: file.type,
                data: base64Data,
                size_bytes: file.size,
              },
            ],
          }),
        });

        const data = await response.json();
        if (data.conversation_id) setConversationId(data.conversation_id);
        setConnectionStatus('connected');

        const nexusMsg: ChatMessage = {
          id: `nexus-${Date.now()}`,
          role: 'nexus',
          content: data.response || 'Analiza dokumentu zakonczona.',
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, nexusMsg]);
      } catch (err) {
        console.error('Document analysis failed:', err);
        const errorMsg: ChatMessage = {
          id: `nexus-${Date.now()}`,
          role: 'nexus',
          content: 'Przepraszam, nie udalo mi sie przeanalizowac tego dokumentu. Sprobuj ponownie.',
          timestamp: new Date(),
          status: 'error',
        };
        setMessages((prev) => [...prev, errorMsg]);
      } finally {
        setIsTyping(false);
        e.target.value = '';
      }
      return;
    }

    // For images/videos: existing vision API flow
    setIsTyping(true);

    // Convert to base64 data URL
    const reader = new FileReader();
    const mediaBase64 = await new Promise<string>((resolve) => {
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(file);
    });

    // Show user message
    const userMsg: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: isVideo
        ? `\uD83C\uDFAC Wyslano video: ${file.name}`
        : `\uD83D\uDDBC\uFE0F Wyslano obraz: ${file.name}`,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMsg]);

    try {
      const authHeaders = await getAuthHeaders();

      const response = await fetchWithRetry(`${OPENCLAW_API_URL}/api/openclaw/vision`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
        },
        body: JSON.stringify({
          tenant_id: effectiveTenantId,
          user_id: user.id,
          media_url: mediaBase64,
          media_type: isVideo ? 'video' : 'image',
          mime_type: file.type,
          task: {
            type: 'analyze',
            prompt:
              'Przeanalizuj to co widzisz i opisz szczegolowo po polsku. Jesli to produkt — ocen jakosc. Jesli to dokument — wyciagnij kluczowe informacje.',
          },
        }),
      });

      const data = await response.json();

      const nexusMsg: ChatMessage = {
        id: `nexus-${Date.now()}`,
        role: 'nexus',
        content: data.analysis?.description || data.response || 'Analiza zakonczona.',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, nexusMsg]);
    } catch (err) {
      console.error('Vision analysis failed:', err);
      const errorMsg: ChatMessage = {
        id: `nexus-${Date.now()}`,
        role: 'nexus',
        content: 'Przepraszam, nie udalo mi sie przeanalizowac tego pliku. Sprobuj ponownie.',
        timestamp: new Date(),
        status: 'error',
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setIsTyping(false);
      e.target.value = ''; // reset file input
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Proactive briefing (inline mode): once the tenant context is known and the
  // conversation is empty (idle), auto-send ONE role-appropriate opening prompt
  // as a real Nexus turn so the assistant greets first. Reuses the exact send
  // path above — no fabricated briefing.
  const briefingSentRef = useRef(false);
  useEffect(() => {
    if (!inline || !autoBriefingPrompt) return;
    if (briefingSentRef.current) return;
    if (!user || !effectiveTenantId) return;
    if (isTyping) return;
    briefingSentRef.current = true;
    void sendMessage(autoBriefingPrompt);
    // Intentionally runs once when tenant context becomes available (guarded by
    // briefingSentRef); sendMessage/isTyping are deliberately not dependencies.
  }, [inline, autoBriefingPrompt, user, effectiveTenantId]);

  const handleRetryConnection = () => {
    setConnectionStatus('connecting');
    checkHealth();
  };

  // Don't render for unauthenticated users
  if (!user) return null;

  // Clamp do viewportu: na wąskich ekranach (globalny widget renderuje się na
  // każdej stronie) okno nie może wystawać poza krawędź ani pod klawiaturę.
  const chatWidth = isExpanded
    ? 'w-[min(500px,calc(100vw-1.5rem))]'
    : 'w-[min(380px,calc(100vw-1.5rem))]';
  const chatHeight = isExpanded ? 'h-[min(600px,80dvh)]' : 'h-[min(480px,70dvh)]';

  const statusLabel =
    connectionStatus === 'connected'
      ? 'Online'
      : connectionStatus === 'connecting'
        ? 'Łączenie...'
        : 'Offline';
  const statusColor =
    connectionStatus === 'connected'
      ? 'text-emerald-400'
      : connectionStatus === 'connecting'
        ? 'text-amber-400'
        : 'text-red-400';
  const statusDotColor =
    connectionStatus === 'connected'
      ? 'bg-emerald-400'
      : connectionStatus === 'connecting'
        ? 'bg-amber-400'
        : 'bg-red-400';

  // Inline embed (e.g. live Jarvis page): render the conversational surface in
  // a static, full-width container instead of the fixed floating orb. The
  // window chrome (drag/expand/close/orb) is only meaningful when floating.
  // Artifact mode (Kokpit universum): just a translucent glass composer bar —
  // the host renders the conversation as floating artifacts.
  const windowClassName = artifactMode
    ? 'w-full flex flex-col bg-gray-950/60 backdrop-blur-xl border border-purple-500/25 rounded-ids-xl shadow-elev-4 overflow-hidden'
    : inline
      ? 'w-full h-[560px] flex flex-col bg-aurora-bg border border-purple-500/30 rounded-ids-xl shadow-elev-4 overflow-hidden'
      : `fixed bottom-20 right-3 sm:right-6 z-[100] ${chatWidth} ${chatHeight} flex flex-col bg-aurora-bg border border-purple-500/30 rounded-ids-xl shadow-elev-4 overflow-hidden transition-all duration-300`;

  return (
    <>
      {/* Chat Window */}
      {isOpen && (
        <div className={windowClassName}>
          {/* Header — hidden in artifact mode (the universe IS the surface) */}
          {!artifactMode && (
            <div className="flex items-center justify-between px-4 py-3 bg-aurora-bg-subtle border-b border-aurora-border">
              <div className="flex items-center gap-3">
                <div className="relative">
                  {/* Rdzeń Nexusa — .jcore (radialna „kula-dusza" + corebeat) */}
                  <div className="jcore flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-aurora-on-accent" />
                  </div>
                  <span
                    className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 ${statusDotColor} rounded-full border-2 border-gray-900 ${connectionStatus === 'connecting' ? 'animate-pulse' : ''}`}
                  />
                </div>
                <div>
                  <div className="text-sm font-semibold text-aurora-text font-display">NEXUS</div>
                  <div className={`text-xs ${statusColor} flex items-center gap-1`}>
                    {connectionStatus === 'offline' && <WifiOff className="w-3 h-3" />}
                    {statusLabel}
                    {isPlayingAudio && ' • Mówię...'}
                    {isRecording && ' • Nagrywam...'}
                    {!isPlayingAudio && !isRecording && ' • Tekst, głos, obraz'}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {connectionStatus === 'offline' && (
                  <button
                    onClick={handleRetryConnection}
                    className="p-1.5 text-aurora-muted hover:text-aurora-text transition-colors rounded-ids-sm hover:bg-aurora-surface"
                    title="Ponów połączenie"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                )}
                {!inline && (
                  <>
                    <button
                      onClick={() => setIsExpanded(!isExpanded)}
                      className="p-1.5 text-aurora-muted hover:text-aurora-text transition-colors rounded-ids-sm hover:bg-aurora-surface"
                    >
                      {isExpanded ? (
                        <Minimize2 className="w-4 h-4" />
                      ) : (
                        <Maximize2 className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      onClick={() => setIsOpen(false)}
                      className="p-1.5 text-aurora-muted hover:text-aurora-text transition-colors rounded-ids-sm hover:bg-aurora-surface"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Super-admin scope selector: single tenant or the whole platform.
              Partner NIE ma tu wyboru rozmówcy — ten wiersz jest za `isSuperAdmin`
              i pojawia się także wtedy, gdy super-admin oglada panel partnera.
              W panelu partnera podpisujemy go wprost, bo inaczej wygląda jak
              „nexus partnera z wyborem kontekstu”, którego być nie może:
              w podglądzie rozmawia NEXUS super-admina. */}
          {isSuperAdmin && (
            <div className="px-4 py-2 bg-aurora-bg-subtle border-b border-aurora-border flex items-center gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-[.09em] text-aurora-subtle whitespace-nowrap">
                {window.location.pathname.startsWith('/partner')
                  ? 'NEXUS super-admina — zakres:'
                  : 'Zakres:'}
              </span>
              <select
                value={saScopeOrgId}
                onChange={(e) => handleScopeChange(e.target.value)}
                className="flex-1 min-w-0 px-2 py-1 bg-aurora-surface border border-aurora-border rounded-ids-sm text-xs text-aurora-text focus:outline-none focus:border-purple-500"
                title="Tenant, w którego kontekście działa NEXUS, albo „Wszyscy tenanci” — perspektywa całej platformy (historia rozmowy zapisuje się wtedy w organizacji platformowej)"
              >
                {saOrgs.length === 0 && <option value="">Ładowanie…</option>}
                {saOrgs.length > 0 && (
                  <option value={PLATFORM_SCOPE}>🌐 Wszyscy tenanci (platforma)</option>
                )}
                {saOrgs.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Connection banner when offline */}
          {connectionStatus === 'offline' && (
            <div className="px-4 py-2 bg-red-500/10 border-b border-red-500/20 flex items-center gap-2">
              <WifiOff className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
              <span className="text-xs text-red-300">
                Serwer NEXUS niedostępny. Próbuję ponownie automatycznie.
              </span>
              <button
                onClick={handleRetryConnection}
                className="ml-auto text-xs text-red-400 hover:text-red-300 flex items-center gap-1 flex-shrink-0"
              >
                <RefreshCw className="w-3 h-3" />
                Ponów
              </button>
            </div>
          )}

          {/* Messages — hidden in artifact mode (host renders them as artifacts) */}
          {!artifactMode && (
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] px-4 py-2.5 rounded-ids-xl text-sm leading-relaxed ${
                      msg.role === 'user'
                        ? // tint marki jak .jmsg.user (organism.css) — nie niebieski relikt
                          'bg-[rgba(var(--ids-brand-rgb),.18)] border border-[rgba(var(--ids-brand-rgb),.3)] text-aurora-text rounded-br-ids-sm'
                        : msg.status === 'error'
                          ? 'bg-red-500/10 text-aurora-text border border-red-500/30 rounded-bl-ids-sm'
                          : 'bg-aurora-surface text-aurora-text border border-aurora-border rounded-bl-ids-sm'
                    }`}
                  >
                    {msg.role === 'nexus' && (
                      <div className="flex items-center gap-1.5 mb-1">
                        <Sparkles
                          className={`w-3 h-3 ${msg.status === 'error' ? 'text-red-400' : 'text-purple-400'}`}
                        />
                        <span
                          className={`text-xs font-medium ${msg.status === 'error' ? 'text-red-400' : 'text-purple-400'}`}
                        >
                          NEXUS
                        </span>
                      </div>
                    )}
                    {msg.content}
                    {GENERATIVE_UI_ENABLED && msg.role === 'nexus' && msg.directives != null && (
                      <GenerativeSurface directives={msg.directives} />
                    )}
                  </div>
                </div>
              ))}
              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-aurora-surface border border-aurora-border rounded-ids-xl rounded-bl-ids-sm px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <Sparkles className="w-3 h-3 text-purple-400 animate-pulse" />
                      <div className="flex gap-1">
                        <span
                          className="w-2 h-2 bg-purple-400 rounded-full animate-bounce"
                          style={{ animationDelay: '0ms' }}
                        />
                        <span
                          className="w-2 h-2 bg-purple-400 rounded-full animate-bounce"
                          style={{ animationDelay: '150ms' }}
                        />
                        <span
                          className="w-2 h-2 bg-purple-400 rounded-full animate-bounce"
                          style={{ animationDelay: '300ms' }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}

          {/* Input area — voice-first .jvoice (prototyp: Aurora DS.html 447-450 +
              organism.css .jvoice__mic): duży okrągły mikrofon z fioletową
              poświatą po aktywacji, kolumna input + .jvoice__hint ze stanem,
              akcje jako .iconbtn. Handlery, disabled i refy bez zmian. */}
          <div className="jvoice">
            {/* Quick voice message — mikrofon (aria-pressed = nagrywanie) */}
            <button
              onClick={isRecording ? stopRecording : startRecording}
              disabled={isTyping}
              className="jvoice__mic disabled:opacity-40"
              aria-pressed={isRecording}
              title={isRecording ? 'Zatrzymaj nagrywanie' : 'Wyślij wiadomość głosową'}
            >
              {isRecording ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
            </button>

            {/* Text input + hint stanu (nagrywanie / zaproszenie do mówienia) */}
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={isRecording ? 'Nagrywam...' : 'Napisz do NEXUS...'}
                className="w-full px-3 py-2 bg-aurora-bg-subtle border border-aurora-border rounded-ids-md text-sm text-aurora-text placeholder:text-aurora-subtle focus:outline-none focus:border-purple-500 transition-colors"
                disabled={isRecording}
              />
              <span className="jvoice__hint" aria-live="polite">
                {isRecording
                  ? 'Nagrywam... kliknij aby zatrzymać'
                  : t('nexus.voiceHint', 'Naciśnij mikrofon, aby mówić z Nexusem')}
              </span>
            </div>

            {/* File upload button */}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isTyping || isRecording}
              className="iconbtn disabled:opacity-30"
              title="Wyslij plik (obraz, video, PDF, DOCX, XLSX, TXT...)"
            >
              <Paperclip className="w-4 h-4" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*,.pdf,.docx,.doc,.xlsx,.xls,.csv,.txt,.json,.xml,.html,.md,.rtf"
              className="hidden"
              onChange={handleFileUpload}
            />

            {/* Voice conversation mode button */}
            <button
              onClick={() =>
                effectiveTenantId ? setIsVoiceSessionOpen(true) : reportMissingTenant()
              }
              disabled={isTyping || isRecording}
              className="iconbtn disabled:opacity-30"
              title="Rozmowa głosowa"
            >
              <AudioLines className="w-4 h-4" />
            </button>

            {/* Send button */}
            <button
              onClick={() => sendMessage()}
              disabled={!input.trim() || isTyping || isRecording}
              className="iconbtn disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {isTyping ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      )}

      {/* Floating Button — only in floating mode; the inline embed has no orb */}
      {!inline && (
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`fixed bottom-6 right-6 z-[100] group transition-all duration-300 ${
            isOpen ? 'scale-90' : 'scale-100 hover:scale-110'
          }`}
        >
          <div className="relative">
            {/* Glow ring — kolory tożsamości Nexusa z tokenów (violet/emerald) */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-[var(--violet-500)] to-[var(--emerald-500)] opacity-50 blur-md group-hover:opacity-75 animate-pulse transition-opacity" />
            {/* Orb — .jcore: radialna „kula-dusza" + poświata + corebeat
                (wyłączany przy prefers-reduced-motion w components.css) */}
            <div className="jcore relative w-14 h-14 flex items-center justify-center">
              {isOpen ? (
                <X className="w-6 h-6 text-aurora-on-accent" />
              ) : (
                <Sparkles className="w-6 h-6 text-aurora-on-accent" />
              )}
            </div>
            {/* Status indicator */}
            {!isOpen && (
              <span
                className={`absolute top-0 right-0 w-4 h-4 ${statusDotColor} rounded-full border-2 border-gray-900 ${
                  connectionStatus === 'connected' ? 'animate-pulse' : ''
                }`}
              />
            )}
          </div>
        </button>
      )}

      {/* Voice Conversation Session Overlay */}
      {isVoiceSessionOpen && user && effectiveTenantId && (
        <NexusVoiceSession
          tenantId={effectiveTenantId}
          userId={user.id}
          conversationId={conversationId}
          onConversationId={setConversationId}
          onClose={() => setIsVoiceSessionOpen(false)}
          getAuthHeaders={getAuthHeaders}
        />
      )}
    </>
  );
}
